/**
 * The Notifications row in settings (D310). Turning it on asks the phone's permission. When
 * it cannot be turned on, the switch is greyed and the line under it says why: on iPhone,
 * that the app must be added to the home screen first. While the server has no VAPID keys
 * (push is off there), the row is not shown at all.
 */
import { useEffect, useState } from 'preact/hooks';
import { Switch } from './Switch.tsx';
import { showInstallPromptNow } from './install.ts';
import {
  PUSH_ABOUT,
  PUSH_BLOCK_WORDS,
  pushBlock,
  pushIsOn,
  pushKey,
  readPushEnv,
  turnPushOff,
  turnPushOn,
  type PushBlock,
} from './notifications.ts';

export function NotificationsSetting() {
  const [available, setAvailable] = useState(false);
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [block, setBlock] = useState<PushBlock | null>(() => pushBlock(readPushEnv()));

  useEffect(() => {
    let live = true;
    void pushKey()
      .then(async (key) => {
        if (!live || !key) return;
        setAvailable(true);
        if (!block) setOn(await pushIsOn());
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  const change = async (want: boolean) => {
    if (busy || want === on) return;
    setBusy(true);
    try {
      if (want) {
        const stopped = await turnPushOn();
        setBlock(stopped);
        setOn(!stopped);
      } else {
        await turnPushOff();
        setOn(false);
      }
    } catch {
      setBlock(pushBlock(readPushEnv()) ?? 'off_on_server');
    } finally {
      setBusy(false);
    }
  };

  if (!available) return null;
  const reason = block ? PUSH_BLOCK_WORDS[block] : undefined;
  return (
    <div style={{ display: 'grid', gap: 2 }} data-testid="notifications-setting">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ font: '600 var(--fs-name)/1.2 var(--font-ui)' }}>Notifications</span>
        <Switch
          label="Notifications"
          on={on}
          onChange={(v) => void change(v)}
          disabled={!!block && !on}
          reason={reason}
        />
      </div>
      <span style={{ font: '400 var(--fs-caption)/1.4 var(--font-ui)', color: 'var(--ink-2)' }}>
        {reason ?? PUSH_ABOUT}
        {block === 'ios_home_screen' && (
          <>
            {' '}
            <button
              type="button"
              data-testid="notifications-install-link"
              onClick={showInstallPromptNow}
              style={{
                border: 'none',
                background: 'transparent',
                padding: 0,
                font: 'inherit',
                color: 'var(--accent)',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              See the steps
            </button>
          </>
        )}
      </span>
    </div>
  );
}
